/*------------------------------------------------------------------------------
   About      : Renders template text with `${CODE}` tokens highlighted
                (epic DA-697, II-FE-2)

                Used by the list's content column and, later, by the preview
                pane (DA-2394). Highlighting is not decoration — it is how an
                author sees at a glance that a token is a token, which is the
                only defence against the `{{value}}` mistake the mockups invite.

                An unknown token renders in error styling rather than plain, and
                a `dateFormatSuffix` variable used bare renders as an error too:
                a bare `${BILL_END}` throws out of pingpong's insight
                computation, so it must never look fine here.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Fragment, useMemo } from 'react'
import { Box, Tooltip }      from '@mui/material'

import { INSIGHT_TOKEN_PATTERN,
         displayNameFor,
         resolveToken }      from '../utils/insightCatalogHelpers'
import { InsightCatalog }    from '../types/insightTypes'

interface TemplateTokenTextProps {
  text      : string | null | undefined
  catalog  ?: InsightCatalog
  /** Single-line with ellipsis, for table cells. */
  truncate ?: boolean
}

type Segment =
  | { kind : 'text'; value : string }
  | { kind : 'token'; raw : string; label : string; problem?: string }

/**
 * Splits text into literal and token segments in one pass.
 *
 * Built with `matchAll` over a shared pattern rather than by `split`, because
 * split would discard the offsets needed to keep literal text between adjacent
 * tokens intact.
 */
function segment(text: string, catalog?: InsightCatalog): Segment[] {
  const segments : Segment[] = []
  let cursor = 0

  // The shared regex is global; matchAll on a fresh iterator avoids the
  // lastIndex leakage a reused .exec loop would cause across calls.
  for (const match of text.matchAll(INSIGHT_TOKEN_PATTERN)) {
    const start = match.index ?? 0
    if (start > cursor) segments.push({ kind : 'text', value : text.slice(cursor, start) })

    const raw      = match[1]
    const resolved = resolveToken(catalog, raw)

    let problem : string | undefined
    if (!catalog) {
      problem = undefined // cannot judge without a catalog; render as a plain token
    } else if (!resolved.known) {
      // Named by its consequence, not by the service that causes it — an author
      // cannot act on "pingpong", only on "customers will see this".
      problem = `Unknown variable "${raw}" — users would see this token exactly as written`
    } else if (resolved.bareDateToken) {
      problem = `${resolved.code} needs a date format suffix; a bare token fails insight computation`
    }

    const label = resolved.known && catalog
      ? displayNameFor(catalog, 'derivedQuantity', resolved.code) +
        (resolved.dateFormat ? ` (${resolved.dateFormat})` : '')
      : raw

    segments.push({ kind : 'token', raw, label, problem })
    cursor = start + match[0].length
  }

  if (cursor < text.length) segments.push({ kind : 'text', value : text.slice(cursor) })
  return segments
}

export const TemplateTokenText = ({ text, catalog, truncate }: TemplateTokenTextProps) => {

  const segments = useMemo(() => (text ? segment(text, catalog) : []), [text, catalog])

  if (!text) {
    return <Box component="span" sx={{ color : 'text.disabled', fontStyle : 'italic' }}>No template text</Box>
  }

  return (
    <Box
      component="span"
      sx={truncate ? {
        display      : 'block',
        overflow     : 'hidden',
        textOverflow : 'ellipsis',
        whiteSpace   : 'nowrap',
      } : undefined}
    >
      {segments.map((seg, index) => {
        if (seg.kind === 'text') return <Fragment key={index}>{seg.value}</Fragment>

        const chip = (
          <Box
            component="span"
            sx={{
              fontFamily   : 'monospace',
              fontSize     : '0.8125rem',
              px           : 0.5,
              borderRadius : 0.5,
              bgcolor      : seg.problem ? 'error.light' : 'primary.lighter',
              color        : seg.problem ? 'error.contrastText' : 'primary.dark',
              border       : seg.problem ? '1px solid' : 'none',
              borderColor  : 'error.main',
            }}
          >
            {`\${${seg.raw}}`}
          </Box>
        )

        return (
          <Tooltip key={index} title={seg.problem || seg.label} placement="top" arrow>
            {chip}
          </Tooltip>
        )
      })}
    </Box>
  )
}

export default TemplateTokenText
